// #tag: GOOGLE OAUTH SIGN-IN BUTTON
// #purpose: Renders Google-branded OAuth sign-in trigger, initiates Firebase popup flow, and dispatches session initialization.
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthProvider';
import { isFirebaseConfigured } from '../../lib/firebase';
import type { AuthUser } from '../../types';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

// #tag: GOOGLE AUTHENTICATION BUTTON COMPONENT
// #purpose: Handles Google popup authentication lifecycle, loading state, error display, and success callbacks.
export function GoogleSignInButton({ onSignedIn }: { onSignedIn: (user: AuthUser) => void }) {
  const { loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isFirebaseConfigured) return null;

  const onClick = async () => {
    setError(null);
    setLoading(true);
    try {
      const signedInUser = await loginWithGoogle();
      onSignedIn(signedInUser);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (/popup-closed|cancelled|popup_closed|popup-blocked/i.test(message)) {
        /* user dismissed */
      } else {
        setError('Could not sign in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-divider"><span>or</span></div>
      {error && <div className="auth-error" role="alert">{error}</div>}
      <button type="button" className="google-button" onClick={onClick} disabled={loading}>
        <GoogleIcon />
        {loading ? 'Connecting…' : 'Continue with Google'}
      </button>
    </>
  );
}
